import { computed } from 'vue';
import { useTableEditorStore } from '../model/table-editor.store';
import TableAddRowActionIcon from '@/shared/assets/icons/TableAddRowActionIcon.svg';
import TableAddColumnActionIcon from '@/shared/assets/icons/TableAddColumnActionIcon.svg';
import TableMergeCellsIcon from '@/shared/assets/icons/TableMergeCellsIcon.svg';
import TableSplitCellsIcon from '@/shared/assets/icons/TableSplitCellsIcon.svg';
import TableTrashIcon from '@/shared/assets/icons/TableTrashIcon.svg';

export type MenuVariant = 'row' | 'column' | 'multi' | 'table' | 'cell';

export type ActionId =
  | 'add-row-above'
  | 'add-row-below'
  | 'add-column-left'
  | 'add-column-right'
  | 'merge-cells'
  | 'unmerge-cells'
  | 'delete-row'
  | 'delete-column'
  | 'delete-table'
  | 'delete-cell-elements'
  | 'delete-cell-element';

export interface TableMenuActionEmit {
  (e: 'add-row-above'): void;
  (e: 'add-row-below'): void;
  (e: 'add-column-left'): void;
  (e: 'add-column-right'): void;
  (e: 'merge-cells'): void;
  (e: 'unmerge-cells'): void;
  (e: 'delete-row'): void;
  (e: 'delete-column'): void;
  (e: 'delete-table'): void;
  (e: 'delete-cell-elements'): void;
  (e: 'delete-cell-element'): void;
}

export interface ActionMeta {
  label: string;
  icon: string | object;
  danger?: boolean;
  iconTransform?: 'flip-x' | 'flip-y' | 'flip-xy';
}

const MERGE_CELLS: ActionId = 'merge-cells';
const UNMERGE_CELLS: ActionId = 'unmerge-cells';
const MERGE_ACTIONS: ActionId[] = [MERGE_CELLS, UNMERGE_CELLS];

export const ACTION_META: Record<ActionId, ActionMeta> = {
  'add-row-above': { label: 'Adicionar linha acima', icon: TableAddRowActionIcon },
  'add-row-below': {
    label: 'Adicionar linha abaixo',
    icon: TableAddRowActionIcon,
    iconTransform: 'flip-y',
  },
  'add-column-left': { label: 'Adicionar coluna à esquerda', icon: TableAddColumnActionIcon },
  'add-column-right': {
    label: 'Adicionar coluna à direita',
    icon: TableAddColumnActionIcon,
    iconTransform: 'flip-xy',
  },
  'merge-cells': { label: 'Mesclar células', icon: TableMergeCellsIcon },
  'unmerge-cells': { label: 'Separar células', icon: TableSplitCellsIcon },
  'delete-row': { label: 'Deletar linha', icon: TableTrashIcon, danger: true },
  'delete-column': { label: 'Deletar coluna', icon: TableTrashIcon, danger: true },
  'delete-table': { label: 'Deletar tabela', icon: TableTrashIcon, danger: true },
  'delete-cell-elements': { label: 'Deletar elementos', icon: TableTrashIcon, danger: true },
  'delete-cell-element': { label: 'Deletar elemento', icon: TableTrashIcon, danger: true },
};

/**
 * Action groups + dispatch for the table cell-selection menu, per selection
 * variant. The emit is passed in so the literal-typed dispatch lives here
 * instead of being duplicated in the component.
 */
export function useTableMenuActions(variant: () => MenuVariant, emit: TableMenuActionEmit) {
  const tableEditorStore = useTableEditorStore();

  const canMerge = computed(() => tableEditorStore.selectedCells.length > 1);
  const canUnmerge = computed(() =>
    tableEditorStore.selectedCellsData.some((c) => c.mergeGroupId)
  );
  const hasCellContent = computed(() => Boolean(tableEditorStore.selectedCellData?.content));
  const hasAnyCellContent = computed(() =>
    tableEditorStore.selectedCellsData.some((c) => Boolean(c.content))
  );

  const canDeleteColumnFromSingleCell = computed(() => {
    const table = tableEditorStore.activeTable;
    if (!table || tableEditorStore.selectedCells.length !== 1) return false;
    return table.properties.rows === 1 && table.properties.columns >= 2;
  });

  const canDeleteRowFromSingleCell = computed(() => {
    const table = tableEditorStore.activeTable;
    if (!table || tableEditorStore.selectedCells.length !== 1) return false;
    return table.properties.columns === 1 && table.properties.rows >= 2;
  });

  const actionGroups = computed<ActionId[][]>(() => {
    if (variant() === 'row') {
      return [['add-row-above', 'add-row-below'], MERGE_ACTIONS, ['delete-row']];
    }

    if (variant() === 'column') {
      return [['add-column-left', 'add-column-right'], MERGE_ACTIONS, ['delete-column']];
    }

    if (variant() === 'table') {
      return [['delete-table']];
    }

    if (variant() === 'cell') {
      const groups: ActionId[][] = [];
      if (canUnmerge.value) groups.push([UNMERGE_CELLS]);
      if (hasCellContent.value) groups.push(['delete-cell-element']);
      if (canDeleteColumnFromSingleCell.value) groups.push(['delete-column']);
      if (canDeleteRowFromSingleCell.value) groups.push(['delete-row']);
      return groups;
    }

    const groups: ActionId[][] = [MERGE_ACTIONS];
    if (hasAnyCellContent.value) groups.push(['delete-cell-elements']);
    return groups;
  });

  const isActionDisabled = (action: ActionId): boolean => {
    if (action === MERGE_CELLS) return !canMerge.value;
    if (action === UNMERGE_CELLS) return !canUnmerge.value;
    return false;
  };

  const emitAction = (action: ActionId): void => {
    if (isActionDisabled(action)) return;

    switch (action) {
      case 'add-row-above':
        return emit('add-row-above');
      case 'add-row-below':
        return emit('add-row-below');
      case 'add-column-left':
        return emit('add-column-left');
      case 'add-column-right':
        return emit('add-column-right');
      case 'merge-cells':
        return emit('merge-cells');
      case 'unmerge-cells':
        return emit('unmerge-cells');
      case 'delete-row':
        return emit('delete-row');
      case 'delete-column':
        return emit('delete-column');
      case 'delete-table':
        return emit('delete-table');
      case 'delete-cell-elements':
        return emit('delete-cell-elements');
      case 'delete-cell-element':
        return emit('delete-cell-element');
    }
  };

  return { actionGroups, actionMeta: ACTION_META, isActionDisabled, emitAction };
}
